"use client";

import { cn } from "~/lib/utils";
import Heading from "~/componentsDS/Typography/Heading";
import Paragraph from "~/componentsDS/Typography/Paragraph";
import { InteractiveTimelineStep } from "./types";
import ProgressLine from "./ProgressLine";
import TimelineDot from "./TimelineDot";

type TimelineNavProps = {
    steps: InteractiveTimelineStep[];
    activeStep: number;
    onStepClick: (index: number) => void;
    panelId: string;
};

const TimelineNav = ({ steps, activeStep, onStepClick, panelId }: TimelineNavProps) => {
    return (
        <div className="relative hidden lg:flex flex-col h-full">
            <ProgressLine />

            <div role="tablist" aria-label="Timeline weeks" className="flex flex-col justify-between h-full">
                {steps.map((step, i) => {
                    const isActive = activeStep === i;

                    return (
                        <button
                            key={step.week}
                            type="button"
                            role="tab"
                            onClick={() => onStepClick(i)}
                            aria-selected={isActive}
                            aria-controls={panelId}
                            className={cn(
                                "group/step relative flex items-start gap-4 text-left cursor-pointer",
                                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary300 focus-visible:ring-offset-2 rounded-lg",
                            )}
                        >
                            <TimelineDot isActive={isActive} showHoverPulse className="shrink-0 z-[1]" />

                            <div className="flex flex-col gap-2 pt-2">
                                <Paragraph
                                    weight="500"
                                    className={cn(
                                        "text-[13.68px] leading-[1.25] tracking-[1.094px] uppercase transition-colors duration-300 pb-2",
                                        isActive ? "text-primary500" : "text-gray400 group-hover/step:text-primary300"
                                    )}
                                >
                                    {step.week}
                                </Paragraph>

                                <Heading
                                    as="h3"
                                    size="xs"
                                    weight="400"
                                    className={cn(
                                        "transition-colors duration-300",
                                        isActive ? "text-primary500" : "text-gray400 group-hover/step:text-primary300"
                                    )}
                                >
                                    {step.nav.title}
                                </Heading>

                                {step.nav.description && (
                                    <div
                                        className={cn(
                                            "grid transition-[grid-template-rows,opacity] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]",
                                            isActive ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                                        )}
                                    >
                                        <div className="overflow-hidden">
                                            <Paragraph
                                                size="lg"
                                                className="text-primary500"
                                            >
                                                {step.nav.description}
                                            </Paragraph>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default TimelineNav;
